import { useState, useEffect } from "react";

import { isWindow } from "../lib/utils";

/**
 * A custom hook that tracks whether the screen is mobile or desktop size
 * @param {number} mobileBreakpoint - The maximum width in pixels to be considered a mobile device
 * @returns {Object} An object containing the current breakpoint and boolean helpers
 * @example
 * const { width, isMobile, isDesktop, breakpoint } = useBreakpoint();
 */

export function useBreakpoint(mobileBreakpoint = 1024) {
    // Initialize state to store window width and whether the component has mounted
    const [width, setWidth] = useState<number | undefined>(undefined);
    const [isClient, setIsClient] = useState(false); // Flag to check if it's the client-side

    useEffect(() => {
        // Set isClient to true once component mounts on the client
        setIsClient(true);

        // Ensure that we access the window only on the client side
        const handleResize = () => {
            if (isWindow()) {
                setWidth(window.innerWidth);
            }
        };

        // Set event listener for resizing on client-side only
        if (isWindow()) {
            window.addEventListener("resize", handleResize);
            handleResize(); // Initial call to set width
        }

        // Cleanup the event listener on unmount
        return () => {
            if (isWindow()) {
                window.removeEventListener("resize", handleResize);
            }
        };
    }, []);

    // If we're on the server, or the component hasn't mounted yet, return a default value
    if (!isClient || width === undefined) {
        return {
            width: 0,
            isMobile: false,
            isDesktop: true,
            breakpoint: "desktop",
        };
    }

    // Determine current breakpoint after the component mounts
    const isMobile = width <= mobileBreakpoint;
    const isDesktop = !isMobile;

    return {
        width,
        isMobile,
        isDesktop,
        breakpoint: isMobile ? "mobile" : "desktop",
    };
}
